/**
 * Workload — who is carrying what. FR-L2, T-2.18. Redrawn in Wave 15C (C14
 * §10.6): the kit's header with the office picker, figure tiles, the calls and
 * tasks as lists with their own empty states, and the people across the firm as
 * the kit's table.
 *
 * **The screen renders the scope the server says it answered**, and does not
 * assume. `meta.scope` is `mine`, `user` or `firm`: an associate opening this
 * gets their own numbers because `CaseRepo` narrowed them (FR-L4), and the
 * heading says "Your work" rather than "The firm" — a summary that leaked the
 * firm's totals to a role that cannot see the cases behind them would be FR-L4
 * undone by four integers.
 *
 * **The partner view's people are derived, and it says so.** ProDesk cannot
 * list a firm's staff (ADR-0012 §2), so the rows are the people the firm has
 * given work to. A member of staff with no case does not appear, and a screen
 * that presented this as a directory would send somebody to support asking why
 * a colleague is missing.
 *
 * **Unassigned work is its own number.** It is what a partner opens this screen
 * for, and a per-person table that omitted it would make the firm's book look
 * smaller than it is.
 */

import { Alert, Badge, EmptyState, PageHeader, Select, Skeleton, formatDate } from '@fiveox/ui'
import { AlertTriangle, Briefcase, ListChecks, PhoneCall, Users } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import {
  type ListColumn,
  ListTable,
  RecordSection,
  StatusBadge,
  taskStatus,
  useListTable,
} from '../../components/page'
import { CASE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { CATEGORY_LABELS, type StageCategory, whenLabel } from '../../lib/api/cases'
import {
  type Branch,
  type PartnerMeta,
  type WorkloadMeta,
  type WorkloadPanel,
  partnerMeta,
  workload,
  workloadMeta,
} from '../../lib/api/workload'
import { todayIso } from '../Cases/useAction'

function Figure({ label, value, alert }: { label: string; value: number; alert?: boolean }) {
  return (
    <div className="border-border rounded-2xl border bg-card p-4 shadow-sm">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          alert && value > 0 ? 'text-destructive' : 'text-foreground'
        }`}
      >
        {value}
      </p>
    </div>
  )
}

function Panel({ panel, firm }: { panel: WorkloadPanel; firm: boolean }) {
  const categories = Object.entries(panel.by_category) as Array<[StageCategory, number]>
  const today = todayIso()
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Figure label="Open cases" value={panel.open_cases} />
        <Figure label="Overdue" value={panel.overdue} alert />
        <Figure label="Tasks this week" value={panel.tasks_due_this_week} />
        <Figure label="Calls today" value={panel.callbacks_today} />
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2" aria-label="Open cases by stage">
          {categories.map(([category, count]) => (
            <StatusBadge
              key={category}
              kind="case"
              status={category}
              label={`${CATEGORY_LABELS[category] ?? category}: ${count}`}
            />
          ))}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <RecordSection
          id="workload-calls"
          icon={<PhoneCall size={18} aria-hidden />}
          title="Calls to make"
          description="Promised call-backs, today and late."
        >
          {panel.callbacks.length === 0 ? (
            <EmptyState
              variant="outline"
              title="No calls to make"
              description={
                firm
                  ? 'Nobody at your firm has a call-back due.'
                  : 'Call-backs you schedule on a case appear here on the day.'
              }
            />
          ) : (
            <ul className="divide-border divide-y">
              {panel.callbacks.map((row) => (
                <li key={row.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  {row.subject_kind === 'case' ? (
                    <Link to={`/cases/${row.subject_id}`} className="text-primary text-sm font-medium hover:underline">
                      {row.subject_label ?? 'A call back'}
                    </Link>
                  ) : (
                    <span className="text-foreground text-sm font-medium">
                      {row.subject_label ?? 'A call back'}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">{whenLabel(row.scheduled_at)}</span>
                    {/* Late, not missed: the clock has passed and nobody has
                        said what happened yet. */}
                    {row.is_late && (
                      <Badge size="sm" tone="error">
                        Late
                      </Badge>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </RecordSection>

        <RecordSection
          id="workload-tasks"
          icon={<ListChecks size={18} aria-hidden />}
          title="Tasks due"
          description="What is due this week, on the cases in view."
        >
          {panel.tasks.length === 0 ? (
            <EmptyState
              variant="outline"
              title="Nothing due this week"
              description="Tasks with a due date in the next seven days appear here."
            />
          ) : (
            <ul className="divide-border divide-y">
              {panel.tasks.map((task) => (
                <li key={task.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                  <div className="min-w-0">
                    <Link
                      to={`/cases/${task.case_id}`}
                      className="text-primary text-sm font-medium hover:underline"
                    >
                      {task.title}
                    </Link>
                    <p className="text-muted-foreground text-xs">
                      {task.due_date ? formatDate(task.due_date) : 'No date'}
                      {task.is_required ? ' · required' : ''}
                    </p>
                  </div>
                  <StatusBadge
                    kind="task"
                    status={taskStatus({ is_done: false, due_date: task.due_date }, today)}
                  />
                </li>
              ))}
            </ul>
          )}
        </RecordSection>
      </div>
    </div>
  )
}

function People({ people }: { people: WorkloadPanel[] }) {
  const columns = useMemo<ListColumn<WorkloadPanel>[]>(
    () => [
      {
        key: 'person',
        label: 'Person',
        width: 140,
        required: true,
        phone: true,
        // Eight characters of the user id — ProDesk has no member directory to
        // name them from (ADR-0012 §2).
        render: (row) => (
          <span className="text-foreground font-mono text-xs">{row.user_id?.slice(0, 8) ?? '—'}</span>
        ),
      },
      {
        key: 'open',
        label: 'Open',
        width: 80,
        align: 'right',
        phone: true,
        sortField: 'open_cases',
        render: (row) => <span className="tabular-nums">{row.open_cases}</span>,
      },
      {
        key: 'status',
        label: 'Overdue',
        width: 90,
        align: 'right',
        phone: true,
        sortField: 'overdue',
        render: (row) => (
          <span className={`tabular-nums ${row.overdue > 0 ? 'text-destructive font-medium' : ''}`}>
            {row.overdue}
          </span>
        ),
      },
      {
        key: 'tasks',
        label: 'Tasks this week',
        width: 120,
        align: 'right',
        sortField: 'tasks_due_this_week',
        render: (row) => <span className="tabular-nums">{row.tasks_due_this_week}</span>,
      },
      {
        key: 'calls',
        label: 'Calls today',
        width: 100,
        align: 'right',
        sortField: 'callbacks_today',
        render: (row) => <span className="tabular-nums">{row.callbacks_today}</span>,
      },
    ],
    [],
  )
  const list = useListTable(people, { columns, initialSort: { field: 'open_cases', dir: 'desc' } })
  return <ListTable list={list} rowKey={(row) => row.user_id ?? 'nobody'} />
}

function Loading() {
  return (
    <div role="status" aria-busy aria-label="Loading" className="space-y-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 rounded-2xl" />
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-48 rounded-2xl" />
      </div>
    </div>
  )
}

export function Workload() {
  const { can } = useEvaluation()
  const [panel, setPanel] = useState<WorkloadPanel | null>(null)
  const [meta, setMeta] = useState<WorkloadMeta | null>(null)
  const [people, setPeople] = useState<WorkloadPanel[]>([])
  const [firm, setFirm] = useState<PartnerMeta | null>(null)
  const [branch, setBranch] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const partnerView = can(CASE, 'can_view_all')

  const load = useCallback(async () => {
    const mine = await workload.mine(branch || undefined)
    setPanel(mine.data)
    setMeta(workloadMeta(mine.meta))
    if (partnerView) {
      const across = await workload.partners(branch || undefined)
      setPeople(across.data)
      setFirm(partnerMeta(across.meta))
    }
    setLoading(false)
  }, [branch, partnerView])

  useEffect(() => {
    setLoading(true)
    void load().catch((err) => {
      setError(err instanceof ApiError ? err.message : 'Your workload could not be loaded.')
      setLoading(false)
    })
  }, [load])

  const branches: Branch[] = meta?.branches ?? []
  const isFirm = meta?.scope === 'firm'

  return (
    <div className="w-full min-w-0 space-y-6">
      <PageHeader
        title="Workload"
        description={
          isFirm
            ? 'What your firm is carrying, and what is late.'
            : 'The cases you are on, what is due this week, and who to ring.'
        }
        actions={
          branches.length > 0 ? (
            <Select
              size="sm"
              aria-label="Office"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
            >
              <option value="">Every office</option>
              {branches.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </Select>
          ) : undefined
        }
      />

      {error && <Alert tone="error">{error}</Alert>}

      {loading && !panel ? (
        <Loading />
      ) : (
        panel && (
          <section aria-labelledby="workload-scope" className="space-y-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 id="workload-scope" className="text-foreground text-base font-semibold">
                <Briefcase size={16} className="text-primary mr-2 inline" aria-hidden />
                {isFirm ? 'The firm' : 'Your work'}
              </h2>
              {meta?.as_at && (
                <span className="text-muted-foreground text-xs">as at {meta.as_at}</span>
              )}
            </div>
            <Panel panel={panel} firm={isFirm} />
          </section>
        )
      )}

      {partnerView && firm && (
        <RecordSection
          id="workload-firm"
          icon={<Users size={18} aria-hidden />}
          title="Across the firm"
          description={
            firm.firm.unassigned > 0 ? (
              <span className="text-destructive inline-flex items-center gap-1 font-medium">
                <AlertTriangle size={14} aria-hidden /> {firm.firm.unassigned} unassigned
              </span>
            ) : (
              'Everything is assigned.'
            )
          }
        >
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <Figure label="Open cases" value={firm.firm.open_cases} />
              <Figure label="Overdue" value={firm.firm.overdue} alert />
              <Figure label="Unassigned" value={firm.firm.unassigned} alert />
              <Figure label="People" value={people.length} />
            </div>

            {people.length === 0 ? (
              <EmptyState
                variant="outline"
                title="Nobody has been given a case yet"
                description="Assign a case from its page and the person appears here with what they are carrying."
                action={
                  firm.firm.unassigned > 0 ? (
                    <Link to="/cases?queue=all" className="text-primary text-sm font-medium hover:underline">
                      See the unassigned cases
                    </Link>
                  ) : undefined
                }
              />
            ) : (
              <People people={people} />
            )}

            {firm.pool_is_derived && (
              <p className="text-muted-foreground text-xs">
                {/* The honest sentence, for the same reason the assign dialog
                    carries it: a firm reading this as a staff list will ask why
                    somebody is missing. */}
                These are the people your firm has given a case to. Members are added to ProDesk
                in the FiveOX console and appear here after their first case.
              </p>
            )}
          </div>
        </RecordSection>
      )}
    </div>
  )
}
